/* eslint-disable indent */
import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {MainLoader, DataGrid, ConfirmationModalPromise} from '@components';
import {GreenButton, StyledH3, WhiteButton} from '@styled';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {Breadcrumb} from 'react-bootstrap';
import styled from 'styled-components';
import {sagaActions} from './redux/saga';
import AddEditUnit from './AddEditUnit';

const StyledHeadRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  .heading {
  }
  .actions {
  }
`;

const RowActions = ({data, onAction}) => {
  return (
    <div>
      <WhiteButton
        onClick={() => onAction('edit', data)}
        style={{margin: '.16em'}}
      >
        <i className="fa fa-edit mr-1" />
        Edit
      </WhiteButton>
      <WhiteButton
        onClick={() => onAction('delete', data)}
        style={{margin: '.16em'}}
      >
        <i className="fa fa-trash mr-1" />
        Delete
      </WhiteButton>
    </div>
  );
};

const ManageUnits = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const remoteData = useSelector((state) => state.calcEngine.units);
  const isLoading = useSelector((state) => state.calcEngine.units.loading);
  const [editRow, setEditRow] = useState(null);
  const [showAddEditModal, setShowAddEditModal] = useState(false);

  const history = useHistory();

  const [pagination, setPagination] = React.useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    sortOn: 'name',
    sortDir: 'DESC',
  });

  useEffect(() => {
    if (
      remoteData.page !== pagination.page ||
      remoteData.pageSize !== pagination.pageSize ||
      remoteData.totalCount !== pagination.totalCount ||
      remoteData.sortOn !== pagination.sortOn ||
      remoteData.sortDir !== pagination.sortDir
    ) {
      setPagination({
        page: remoteData.page,
        pageSize: remoteData.pageSize,
        totalCount: remoteData.totalCount,
        sortOn: remoteData.sortOn,
        sortDir: remoteData.sortDir,
      });
    }
  }, [remoteData]);

  const fetchData = ({
    page = 1,
    pageSize = pagination.pageSize,
    sortOn,
    sortDir,
  }) => {
    dispatch({
      type: sagaActions.FETCH_UNITS,
      payload: {
        values: {
          page,
          pageSize,
          sortOn,
          sortDir,
        },
      },
    });
  };

  const handleDeleteUnit = (remove) => {
    dispatch({
      type: sagaActions.REMOVE_UNIT,
      payload: {
        unitId: remove.id,
        values: {},
      },
    });
  };

  const handleUpdateUnitStatus = (row) => {
    const updateStatusTo = row.status === 'Active' ? 'Inactive' : 'Active';
    dispatch({
      type: sagaActions.UPDATE_UNIT,
      payload: {unitId: row.id, values: {status: updateStatusTo}},
    });
  };

  const handleRowAction = useCallback(
    (action, row) => {
      switch (action) {
        case 'edit': {
          setEditRow(row);
          setShowAddEditModal(true);
          break;
        }
        case 'sources':
          history.push(`/calc-engine/${row.id}/sources`);
          break;
        case 'delete': {
          ConfirmationModalPromise({
            title: `Are you sure you want to delete this [${row.name}] unit?`,
            show: true,
            confirmBtn: 'Yes',
            cancelBtn: 'No',
            headTitle: 'Confirmation!',
          }).then(
            () => {
              handleDeleteUnit(row);
            },
            () => {
              //  do nothing on failure
            }
          );
          break;
        }
        case 'status': {
          ConfirmationModalPromise({
            title: `Are you sure you want to make unit [${
              row.status === 'Active' ? 'inactive' : 'active'
            }]?`,
            show: true,
            confirmBtn: 'Yes',
            cancelBtn: 'No',
            headTitle: 'Confirmation!',
          }).then(
            () => {
              handleUpdateUnitStatus(row);
            },
            () => {
              //  do nothing on failure
            }
          );
          break;
        }

        default:
          break;
      }
    },
    [editRow, showAddEditModal]
  );

  const handleAddUnit = () => {
    setShowAddEditModal(true);
  };

  const handleToggleAddEditModal = useCallback(() => {
    setEditRow(null);
    setShowAddEditModal(false);
  }, [editRow, showAddEditModal]);

  const renderAddEditModal = () => {
    return (
      <AddEditUnit
        show={showAddEditModal}
        editRow={editRow}
        toggleModal={handleToggleAddEditModal}
      />
    );
  };

  const renderRowActions = (colProps) => {
    return <RowActions data={colProps} onAction={handleRowAction} />;
  };

  const Columns = React.useMemo(
    () => [
      {
        accessor: 'id',
        Header: '#',
        className: 'grey-column d-none d-lg-block',
        width: 50,
        tooltip: false,
        hidden: true,
      },
      {
        accessor: 'name',
        Header: 'Unit Name',
        tooltip: false,
        className: 'd-none d-lg-block',
      },
      {
        accessor: 'status',
        Header: 'Status',
        tooltip: false,
        className: 'd-none d-lg-block',
      },
      {
        id: 'actions',
        Header: <div className="d-none d-lg-block">Actions</div>,
        accessor: renderRowActions,
        disableSortBy: true,
        disableFilters: true,
        width: 50,
      },
    ],
    [pagination]
  );

  return (
    <>
      {isLoading && (
        <MainLoader
          minHeight="100vh"
          spinnerColor="#479541"
          background="rgba(0,0,0,0.2)"
        />
      )}
      <div>
        <Breadcrumb>
          <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
          <Breadcrumb.Item href="/calc-engine">
            Source Categories
          </Breadcrumb.Item>
          <Breadcrumb.Item active>Units</Breadcrumb.Item>
        </Breadcrumb>
        <StyledH3>{t('owner.calcEngine.pageHeading')}</StyledH3>
        <StyledHeadRow>
          <div className="heading">
            {t('owner.calcEngine.units.pageHeading')}
          </div>
          <div className="actions">
            <GreenButton className="ml-1" onClick={handleAddUnit}>
              <i className="fa fa-plus mr-1" />
              {t('owner.calcEngine.units.addUnit')}
            </GreenButton>
          </div>
        </StyledHeadRow>
        <DataGrid
          columns={Columns}
          data={remoteData.data}
          onFetchData={fetchData}
          pagination={pagination}
          filtering={false}
          expandEnabled={false}
        />
      </div>
      {renderAddEditModal()}
    </>
  );
};

export default ManageUnits;
